import { Box, IconButton, Select, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { Add, ArrowLeft, ArrowRight, Remove } from '@mui/icons-material';
import TooltipHardened from './TooltipHardened';
import { CenteredColumn } from './Stacks';

interface Props {
  index?: number;
  length?: number;
  /** does not disable remove when length===1 */
  optional?: boolean;
  disableAdd?: boolean;
  disableRemove?: boolean;
  /** the callback to change the current index. Will be called onNext, onBack, onAdd and onRemove */
  onIndexChange: (index: number) => void;
  onAdd?: () => void;
  /** When called with index===length, also calls onIndexChange to prevent overflow */
  onRemove?: () => void;
  /** Enables clicking the current index and selecting another index within available range as a "to" argument */
  onMove?: (to: number) => void;
  /** will align the list stepper to the child content element using a CenteredColumn component */
  content?: React.JSX.Element;
}

export default function ListStepper({
  index,
  length,
  optional,
  disableAdd,
  disableRemove,
  onIndexChange,
  onAdd,
  onRemove,
  onMove,
  content,
}: Props): React.JSX.Element {
  const isOptional = useMemo(() => optional === true, [optional]);
  const backButton = useMemo(
    () => (
      <IconButton
        disabled={[undefined, 0].includes(index)}
        onClick={() => {
          if (index === undefined) {
            return;
          }

          onIndexChange(index - 1);
        }}
      >
        <ArrowLeft fontSize={'inherit'} />
      </IconButton>
    ),
    [index, onIndexChange],
  );

  const forwardButton = useMemo(
    () => (
      <IconButton
        disabled={[index, length].includes(undefined) || index! === length! - 1}
        onClick={() => {
          if (index === undefined || length === undefined) {
            return;
          }

          onIndexChange(index + 1);
        }}
      >
        <ArrowRight fontSize={'inherit'} />
      </IconButton>
    ),
    [index, length, onIndexChange],
  );

  const indexElement = useMemo(() => {
    if (onMove && index !== undefined && length !== undefined && length > 1) {
      const selectableIndices = [...Array(length).keys()];
      const selectOptions = selectableIndices.map((si) => (
        <option key={si} value={si}>
          {(si + 1).toString()}
        </option>
      ));
      return (
        <TooltipHardened tooltip={'change order position of item'}>
          <Select native variant={'standard'} value={index} onChange={(e) => onMove(+e.target.value)}>
            {selectOptions}
          </Select>
        </TooltipHardened>
      );
    }

    return <Typography>{index === undefined ? '-' : (index + 1).toString()}</Typography>;
  }, [index, length, onMove]);

  const indexIndicator = useMemo(
    () => (
      <Stack direction={'row'} spacing={2} alignItems={'center'} sx={{ userSelect: 'none' }}>
        {indexElement}
        <Typography>{'/'}</Typography>
        <Typography>{length === undefined ? '-' : length.toString()}</Typography>
      </Stack>
    ),
    [indexElement, length],
  );

  const addButton = useMemo(() => {
    if (onAdd === undefined) {
      return <></>;
    }

    return (
      <IconButton onClick={onAdd} disabled={disableAdd === true}>
        <TooltipHardened tooltip={'add item'}>
          <Add />
        </TooltipHardened>
      </IconButton>
    );
  }, [onAdd, disableAdd]);

  const removeButton = useMemo(() => {
    if (onRemove === undefined) {
      return <></>;
    }

    return (
      <IconButton
        disabled={[index, length].includes(undefined) || (length === 1 && !isOptional) || disableRemove === true}
        onClick={() => {
          if (index! === length! - 1) {
            onIndexChange(Math.max(0, index! - 1));
          }
          onRemove();
        }}
      >
        <TooltipHardened tooltip={'remove current item'}>
          <Remove />
        </TooltipHardened>
      </IconButton>
    );
  }, [onRemove, onIndexChange, index, length, isOptional]);

  const listStepperElement = useMemo(
    () => (
      <Stack
        direction={'row'}
        justifyContent={'space-between'}
        border={1}
        borderRadius={2}
        borderColor={(theme) => theme.palette.action.disabled}
        sx={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
      >
        {addButton}
        <Stack direction={'row'} spacing={1}>
          {backButton}
          {indexIndicator}
          {forwardButton}
        </Stack>
        {removeButton}
      </Stack>
    ),
    [indexIndicator, addButton, removeButton, backButton, forwardButton],
  );

  return content ? (
    <CenteredColumn width={'100%'} spacing={1}>
      {listStepperElement}
      {content}
    </CenteredColumn>
  ) : (
    <Box sx={{ py: 1 }}>{listStepperElement}</Box>
  );
}
